"use client";

import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, usePathname } from 'next/navigation';

import getActivities, { ActivityType, ACTIVITY_TYPE_ID, fetchLaunchData } from "@/api/activities";
import { readThroughCache } from '@/utils/cache';
import { useIsLoggedIn } from '@/utils/useIsLoggedIn';
import { useError } from '@/context/ErrorContext';
import { setHasDoneWarmupInCache, getHasDoneWarmupFromCache } from '@/app/(auth)/activities/[activity]/[topic]/warmup/userDetailsCache';
import { getUserData, postUserData } from '@/api/userData';
import { useIsC1Integrated } from '@/components/C1Integration/useIsC1Integrated';
import { OpenerContext } from '@/components/C1Opener/OpenerCommunicator';
import { hasSeenPage } from '@/utils/techChecks';
import useUserId from '@/hooks/useUserId';
import { Tip } from '@/api/tips';

/**
 * Session storage cache expiry interval in seconds.
 */
const TIMEOUT_INTERVAL = 300;

function getTimestamp() {
    return Math.floor(Date.now() / 1000);
}

export enum STATUS {
    NOINIT,
    PENDING,
    COMPLETE,
    ERROR
}

interface AppContextState {
    // Activity state
    activities: ActivityType[];
    activitiesStatus: STATUS;
    tip?: Tip;
    
    // User data state
    hasDoneWarmup?: boolean;
    isLoading: boolean;
    userCompletedMicChecks?: boolean;

    // Attempt tracking
    remainingAttempts?: number | null;
    replenishmentDate?: number | null;
    isAttemptsFetched?: boolean;
    userRole?: string;
    context?: string;
    
    // Actions
    refresh: () => void;
}

const defaults: AppContextState = {
    activities: [],
    activitiesStatus: STATUS.NOINIT,
    tip: undefined,
    hasDoneWarmup: undefined,
    isLoading: true,
    userCompletedMicChecks: undefined,
    remainingAttempts: undefined,
    replenishmentDate: undefined,
    userRole: undefined,
    context: undefined,
    refresh: () => {},
};

/**
 * Key used for session storage persistence.
 */
const contextName = 'LaunchData';

/**
 * Initializes context state from session storage or returns defaults.
 * 
 * This function implements a caching strategy with automatic expiry:
 * 1. Checks if cached data exists in sessionStorage
 * 2. Validates cache hasn't expired (TIMEOUT_INTERVAL seconds)
 * 3. Returns cached data if valid, otherwise returns fresh defaults
 * 
 * @returns {Partial<AppContextState>} Initial state from cache or defaults
 */
const createInitialState = () => {
    try {
        const timestamp = window.sessionStorage.getItem(`${contextName}SetTime`) ?? "0";
        const expired = parseInt(timestamp) + TIMEOUT_INTERVAL < getTimestamp();
        if (!expired) {
            const savedData = window.sessionStorage.getItem(contextName);
            if (savedData) {
                const parsed = JSON.parse(savedData);
                return {
                    activities: parsed.activities || [],
                    activitiesStatus: parsed.activitiesStatus || STATUS.NOINIT,
                    tip: parsed.tip,
                    hasDoneWarmup: parsed.hasDoneWarmup,
                    isLoading: parsed.isLoading ?? true,
                    userCompletedMicChecks: parsed.userCompletedMicChecks,
                    remainingAttempts: parsed.remainingAttempts,
                    replenishmentDate: parsed.replenishmentDate,
                    userRole: parsed.userRole,
                    context: parsed.context
                };
            }
        } else {
            window.sessionStorage.removeItem(contextName);
        }
    } catch {
        // Silently fail if sessionStorage is not available (e.g., in SSR or private browsing)
    }

    return {
        activities: [],
        activitiesStatus: STATUS.NOINIT,
        hasDoneWarmup: undefined,
        isLoading: true,
        userCompletedMicChecks: undefined,
        remainingAttempts: undefined,
        replenishmentDate: undefined,
        userRole: undefined,
        context: undefined
    };
};

const AppContext = createContext<AppContextState>(defaults);

export const useAppContext = () => useContext(AppContext);

export const useActivities = () => {
    const { activities, activitiesStatus } = useAppContext();
    return { activities, status: activitiesStatus };
};

export const AppContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState(createInitialState);
    const stateRef = useRef(state);

    /** To refresh attempts data on every launch */
    const [isAttemptsFetched, setIsAttemptsFetched] = useState(false);
    
    // Keep ref in sync with state
    useEffect(() => {
        stateRef.current = state;
    }, [state]);
    
    const isLoggedIn = useIsLoggedIn();
    const pathParams = useParams();
    const pathname = usePathname();
    const { showError } = useError();
    
    const isC1Integrated = useIsC1Integrated();
    const opener = useContext(OpenerContext);
    const userId = useUserId();

    const setAppState = useCallback((updates: Partial<typeof state> | ((prev: typeof state) => Partial<typeof state>)) => {
        setState(prev => {
            const newState = typeof updates === 'function' ? { ...prev, ...updates(prev) } : { ...prev, ...updates };
            
            try {
                const jsonStr = JSON.stringify({
                    activities: newState.activities,
                    activitiesStatus: newState.activitiesStatus,
                    hasDoneWarmup: newState.hasDoneWarmup,
                    isLoading: newState.isLoading,
                    userCompletedMicChecks: newState.userCompletedMicChecks,
                    remainingAttempts: newState.remainingAttempts,
                    replenishmentDate: newState.replenishmentDate,
                    userRole: newState.userRole,
                    context: newState.context
                });
                window.sessionStorage.setItem(contextName, jsonStr);

                if (Object.hasOwn(updates, 'activities')) {
                    window.sessionStorage.setItem(`${contextName}SetTime`, getTimestamp().toString());
                }
            } catch {}
            
            return newState;
        });
    }, []);

    /**
     * Fetch data using the optimized app data endpoint.
     */
    const fetchAppAndUserData = useCallback((
        activityKey: string,
        hasActivities: boolean,
        hasTip: boolean,
        hasUserData: boolean,
        context: string | undefined
    ) => {
        if (!hasActivities) {
            setAppState(() => ({ activitiesStatus: STATUS.PENDING }));
        }

        const defaultUserRole = isC1Integrated
            ? (opener?.initData?.userRole ?? 'c1-user')
            : 'student';
        
        fetchLaunchData(activityKey, !hasActivities, !hasTip, !hasUserData, defaultUserRole, context)
            .then((response) => {
                if (!response) {
                    throw new Error("Failed to launch activity");
                }
                
                const updates: Partial<typeof state> = {
                    userRole: defaultUserRole,
                    context
                };
                
                if (response.activity) {
                    updates.activities = [response.activity];
                    updates.activitiesStatus = STATUS.COMPLETE;
                    
                    const { key, topics: { topics } } = response.activity;
                    readThroughCache(key, topics);
                }
                
                if (response.tip) {
                    response.tip.activity_id = activityKey;
                    sessionStorage.setItem('tip', JSON.stringify(response.tip));
                    updates.tip = response.tip;
                }
                
                if (response.user_data) {
                    updates.hasDoneWarmup = response.user_data.has_done_warmup;
                    setHasDoneWarmupInCache(response.user_data.has_done_warmup);
                }
                
                if (response.warmup_activity) {
                    readThroughCache(ACTIVITY_TYPE_ID.WARMUP, response.warmup_activity);
                }

                updates.remainingAttempts = response.remaining_attempts;
                updates.replenishmentDate = response.replenishment_date;

                updates.isLoading = false;
                setAppState(updates);
                setIsAttemptsFetched(true);
            })
            .catch((error) => {
                setAppState({ 
                    activitiesStatus: STATUS.ERROR, 
                    activities: [],
                    isLoading: false 
                });
                console.error("Failed to fetch activity data:", error);
                showError('Failed to fetch activity data', true);
            });
    }, [setAppState, isC1Integrated, opener?.initData?.userRole, showError]);

    /**
     * Fetch all activities using the individual endpoint.
     */
    const fetchAllActivities = useCallback(() => {
        setAppState(() => ({ activitiesStatus: STATUS.PENDING }));
        
        return getActivities()
            .then((data) => {
                if (!data) {
                    throw new Error("Failed to fetch activities");
                }
                setAppState({ activities: data, activitiesStatus: STATUS.COMPLETE });
                data?.forEach(activity => {
                    const { key, topics: { topics } } = activity;
                    readThroughCache(key, topics);
                });
            })
            .catch((error) => {
                setAppState({ activitiesStatus: STATUS.ERROR, activities: [] });
                console.error("Failed to fetch the activities:", error);
                showError('Failed to fetch the activities', true);
            });
    }, [setAppState, showError]);

    /**
     * Fetch user data or create new user record.
     */
    const fetchUserData = useCallback(() => {
        return getUserData()
            .then((data) => {
                const { has_done_warmup } = data;
                setHasDoneWarmupInCache(has_done_warmup === true);
                setAppState({ hasDoneWarmup: !!has_done_warmup });
            })
            .catch(async (error) => {
                if (typeof error === 'object' && error?.['status'] === 404) {
                    // Mark as new user (hasn't done warmup)
                    setHasDoneWarmupInCache(false);
                    setAppState({ hasDoneWarmup: false });
                    
                    const userType = isC1Integrated 
                        ? opener?.initData?.userRole ?? 'c1-user'
                        : 'cognito-user';
                    
                    try {
                        await postUserData({ userType });
                    } catch (error) {
                        console.error("Failed to create user:", error);
                        showError('Failed to create user', true);
                    }
                } else {
                    console.error("Failed to load the user data:", error);
                    showError('Failed to load the user data', true);
                }
            });
    }, [setAppState, isC1Integrated, opener?.initData?.userRole, showError]);

    /**
     * Intelligently fetches missing application data based on current route and cache state.
     * 
     * 1. Checks what data is already available (activities, tip, user data)
     * 2. Checks session cache for warmup status
     * 3. Decides between batch endpoint (/launch) or individual endpoints
     * 4. Executes appropriate fetch strategy
     */
    const refresh = useCallback(() => {        
        const activityKey = pathParams.activity as string | undefined;
        const topicKey = pathParams.topic as string | undefined;
        
        const hasActivities = activityKey 
            ? stateRef.current.activities?.some(a => a.key === activityKey) 
            : stateRef.current.activities?.length > 0;
        const hasTip = JSON.parse(sessionStorage.getItem('tip') ?? '{}')?.activity_id === activityKey;
        const cachedWarmup = getHasDoneWarmupFromCache();

        if (cachedWarmup !== undefined && stateRef.current.hasDoneWarmup !== cachedWarmup) {
            setAppState({ hasDoneWarmup: cachedWarmup });
        }

        const hasUserData = !!stateRef.current?.hasDoneWarmup || !!cachedWarmup;
        const context = opener?.initData?.context;

        // Use optimized app data endpoint
        if (activityKey && topicKey && (!hasActivities || !hasTip || !hasUserData || (context && !isAttemptsFetched))) {
            fetchAppAndUserData(activityKey, hasActivities, hasTip, hasUserData, context);
        } else {
            // Use individual fetch endpoints
            const promises: Promise<void>[] = [];
            
            // Fetch all activities if we're not on a specific activity page
            if (!activityKey) {
                promises.push(fetchAllActivities());
            }
            
            // Fetch user data if missing and not on warmup page
            if (!hasUserData && !pathname.includes('/warmup')) {
                promises.push(fetchUserData());
            }
            
            Promise.all(promises).finally(() => {
                setAppState({ isLoading: false });
            });
        }
    }, [
        pathParams, 
        pathname,
        opener?.initData?.context,
        isAttemptsFetched,
        fetchAppAndUserData,
        fetchAllActivities,
        fetchUserData,
        setAppState
    ]);

    // Data fetch on route change or login state change
    useEffect(() => {
        if (!isLoggedIn || isC1Integrated === null) {
            return;
        }
        
        refresh();
    }, [isLoggedIn, isC1Integrated, pathname, refresh]);

    // Handle mic checks
    useEffect(() => {
        if (pathname.includes('/checks') || pathname.includes('/transcriptcheck')) {
            setAppState({ userCompletedMicChecks: undefined });
        } else if (userId) {
            const deviceId = navigator.userAgent;
            const hasCompletedFromStorage = hasSeenPage(userId, deviceId);
            setAppState({ userCompletedMicChecks: hasCompletedFromStorage });
        }
    }, [userId, pathname, setAppState]);

    // Set accent in sessionStorage when activity changes
    useEffect(() => {
        const { activity: activityKey } = pathParams;
        if (activityKey && state.activities?.length) {
            const foundActivity = state.activities.find(item => item.key === activityKey);
            sessionStorage.setItem('accent', foundActivity?.accent ?? 'US');
        }
    }, [state.activities, pathParams]);

    const contextValue = useMemo(() => ({
        activities: state.activities,
        activitiesStatus: state.activitiesStatus,
        tip: state.tip,
        hasDoneWarmup: state.hasDoneWarmup,
        isLoading: state.isLoading,
        userCompletedMicChecks: state.userCompletedMicChecks,
        remainingAttempts: state.remainingAttempts,
        replenishmentDate: state.replenishmentDate,
        isAttemptsFetched: isAttemptsFetched,
        userRole: state.userRole,
        context: state.context,
        refresh
    }), [
        state.activities,
        state.activitiesStatus,
        state.tip,
        state.hasDoneWarmup,
        state.isLoading,
        state.userCompletedMicChecks,
        state.remainingAttempts,
        state.replenishmentDate,
        isAttemptsFetched,
        state.userRole,
        state.context,
        refresh
    ]);

    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
};

export const ApplicationContext = AppContext;
export const ApplicationContextProvider = AppContextProvider;
